import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AvatarStack, TrendingRow, VerticalPosterCard, Input, Button } from "@bloom/ui";
import {
  Heart,
  PlayCircle,
  LockKeyhole,
  Gift,
  ShieldCheck,
  Smartphone,
  CircleSlash2,
} from "lucide-react";
import { useLocale } from "../locale/locale-context.tsx";
import { api } from "../api/client.ts";
import {
  FeaturedSeriesBlock,
  type FeaturedSeriesData,
  type FeaturedSeriesEpisode,
} from "../components/landing/featured-series-block.tsx";

interface SeriesItem {
  _id: string;
  slug: string;
  titleTh: string;
  titleEn: string;
  taglineTh: string | null;
  taglineEn: string | null;
  synopsisTh: string;
  synopsisEn: string;
  posterUrl: string;
  categories: string[];
  featured: boolean;
  episodeCount: number;
  year: number | null;
  ageRating: string | null;
  ratingScore: number | null;
  ratingCount: number | null;
  freeEpisodeCount: number | null;
  bundlePriceThb: number | null;
}

// TODO(client-asset): replace placeholder catalog when the client uploads
// real posters via admin. Slots 1–5 match the mockup; slots 6–10 are extras
// to make the trending row's chevron-scroll meaningful.
const PLACEHOLDER_TRENDING: Array<{
  slug: string;
  titleEn: string;
  titleTh: string;
  taglineEn: string;
  taglineTh: string;
  poster: string;
}> = [
  {
    slug: "waterboyy",
    titleEn: "Waterboyy",
    titleTh: "Waterboyy",
    taglineEn: "Hearts at the deep end",
    taglineTh: "รักใสๆ หัวใจนักกีฬา",
    poster: "/posters/placeholder-1.jpg",
  },
  {
    slug: "drip",
    titleEn: "DRIP",
    titleTh: "DRIP",
    taglineEn: "Drop by drop, falling fast",
    taglineTh: "หยดน้ำ...หยุดรัก",
    poster: "/posters/placeholder-2.jpg",
  },
  {
    slug: "secret-love",
    titleEn: "Secret Love",
    titleTh: "ความรักลับ",
    taglineEn: "Hiding the villain in his heart",
    taglineTh: "ปกปิดหัวใจนายตัวร้าย",
    poster: "/posters/placeholder-3.jpg",
  },
  {
    slug: "between-us",
    titleEn: "Between Us",
    titleTh: "ระหว่างเรา",
    taglineEn: "The space between two boys",
    taglineTh: "ระหว่างเรานั้นมีบางอย่าง",
    poster: "/posters/placeholder-4.jpg",
  },
  {
    slug: "my-only-bloom",
    titleEn: "My Only Bloom",
    titleTh: "เธอคนเดียวของฉัน",
    taglineEn: "Just you, beside me",
    taglineTh: "เพียงเธอที่ข้างในใจ",
    poster: "/posters/placeholder-5.jpg",
  },
  {
    slug: "midnight-letters",
    titleEn: "Midnight Letters",
    titleTh: "จดหมายเที่ยงคืน",
    taglineEn: "Words sent after dark",
    taglineTh: "ถ้อยคำที่ส่งให้กันหลังเที่ยงคืน",
    poster: "/posters/placeholder-6.jpg",
  },
  {
    slug: "summer-static",
    titleEn: "Summer Static",
    titleTh: "ฤดูร้อนที่หายไป",
    taglineEn: "A signal he can't tune out",
    taglineTh: "สัญญาณรักที่ไม่อาจตัดออก",
    poster: "/posters/placeholder-7.jpg",
  },
  {
    slug: "wildflower",
    titleEn: "Wildflower",
    titleTh: "ดอกไม้ป่า",
    taglineEn: "Bloomed where no one was watching",
    taglineTh: "บานในที่ที่ไม่มีใครเห็น",
    poster: "/posters/placeholder-8.jpg",
  },
  {
    slug: "neon-summer",
    titleEn: "Neon Summer",
    titleTh: "นีออนซัมเมอร์",
    taglineEn: "City lights, slow kisses",
    taglineTh: "แสงเมือง จูบช้าๆ",
    poster: "/posters/placeholder-9.jpg",
  },
  {
    slug: "after-the-rain",
    titleEn: "After the Rain",
    titleTh: "หลังสายฝน",
    taglineEn: "What stays when the storm leaves",
    taglineTh: "สิ่งที่เหลือเมื่อพายุจากไป",
    poster: "/posters/placeholder-10.jpg",
  },
];

// TODO(client-asset): replace with hero key art from the featured series
const HERO_PORTRAIT_SRCSET = [
  { src: "/hero/hero-placeholder-720.jpg", w: 720 },
  { src: "/hero/hero-placeholder-1080.jpg", w: 1080 },
  { src: "/hero/hero-placeholder-1440.jpg", w: 1440 },
];

// TODO(client-asset): replace with social-proof avatars
const VIEWER_AVATARS = ["/avatars/avatar-1.jpg", "/avatars/avatar-2.jpg", "/avatars/avatar-3.jpg"];

export function LandingPage() {
  const { t, locale } = useLocale();
  const [series, setSeries] = useState<SeriesItem[]>([]);
  const [, setLoading] = useState(true);
  const [featuredEpisodes, setFeaturedEpisodes] = useState<FeaturedSeriesEpisode[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ items: SeriesItem[] }>("/api/series");
        setSeries(data.items);
        const featured = data.items.find((s) => s.featured) ?? data.items[0];
        if (featured) {
          const eps = await api.get<{ items: FeaturedSeriesEpisode[] }>(
            `/api/series/${featured.slug}/episodes`,
          );
          setFeaturedEpisodes(eps.items);
        }
      } catch {
        setSeries([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const apiFeatured = series.find((s) => s.featured) ?? series[0] ?? null;
  // Use API record for data, but force a local placeholder still since seed
  // data uses placehold.co URLs that don't match the mockup mood.
  const featuredSeries: FeaturedSeriesData | null = apiFeatured
    ? {
        ...apiFeatured,
        // TODO(client-asset): swap when real key art is uploaded.
        posterUrl: "/posters/placeholder-1.jpg",
        ratingScore: apiFeatured.ratingScore ?? 4.9,
        ratingCount: apiFeatured.ratingCount ?? 2300,
        year: apiFeatured.year ?? 2024,
        ageRating: apiFeatured.ageRating ?? "18+",
        taglineEn: apiFeatured.taglineEn ?? "Lights up. Hearts in. Don't blink.",
        taglineTh: apiFeatured.taglineTh ?? "ทุกความรู้สึก ทุกสายตา ห้ามกระพริบ",
      }
    : null;

  return (
    <div className="bg-[var(--brand-paper)] text-[var(--brand-ink)]">
      <Hero />
      <Bands>
        <TrendingSection apiItems={series} />
        <PillarsStrip />
        {featuredSeries ? (
          <FeaturedSeriesBlock
            series={featuredSeries}
            episodes={featuredEpisodes}
            locale={locale}
          />
        ) : null}
        <TrustStrip />
        <NewsletterSignup />
      </Bands>
    </div>
  );

  function Hero() {
    return (
      <section className="relative isolate overflow-hidden bg-[var(--brand-pink)]">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 pb-12 pt-24 sm:px-6 sm:pb-16 sm:pt-28 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:px-8 lg:pb-24 lg:pt-32">
          <div className="motion-safe:animate-fade-rise relative z-10 max-w-xl space-y-6">
            <h1 className="font-display text-5xl leading-[0.95] tracking-tight text-[var(--brand-ink)] sm:text-6xl lg:text-7xl">
              {t("landing.hero.title")}
            </h1>
            <p className="max-w-prose text-base text-[var(--brand-ink)] sm:text-lg">
              {t("landing.hero.subtitle")}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={featuredSeries ? `/series/${featuredSeries.slug}` : "/series"}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[var(--brand-ink)] px-7 text-sm font-semibold text-[var(--brand-paper)] hover:opacity-90"
              >
                <PlayCircle className="h-4 w-4 fill-current" aria-hidden />
                {t("landing.hero.cta_play")}
              </Link>
              <Link
                to={featuredSeries ? `/series/${featuredSeries.slug}#trailer` : "/series"}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[var(--brand-ink)] bg-transparent px-7 text-sm font-semibold text-[var(--brand-ink)] hover:bg-white/40"
              >
                ▶ {t("landing.hero.cta_trailer")}
              </Link>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <AvatarStack
                images={VIEWER_AVATARS}
                size="sm"
                ringColorClass="ring-[var(--brand-pink)]"
              />
              <p className="text-sm font-medium text-[var(--brand-ink)]/80">
                {t("landing.hero.followers", { count: "50,000" })}
              </p>
            </div>
          </div>

          {/* Right column — editorial portrait, bleeds to right + top */}
          <div className="relative -mx-4 mt-4 aspect-[4/5] w-[calc(100%+2rem)] overflow-hidden sm:-mx-6 sm:w-[calc(100%+3rem)] lg:absolute lg:inset-y-0 lg:right-0 lg:mx-0 lg:my-0 lg:aspect-auto lg:h-full lg:w-1/2 lg:rounded-l-[3rem]">
            <img
              src={HERO_PORTRAIT_SRCSET[2]!.src}
              srcSet={HERO_PORTRAIT_SRCSET.map((s) => `${s.src} ${s.w}w`).join(", ")}
              sizes="(min-width: 1024px) 50vw, 100vw"
              alt=""
              fetchPriority="high"
              decoding="async"
              className="h-full w-full object-cover object-center"
            />
            {/* Soft pink wash blending into the page background on the left edge */}
            <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-1/3 bg-gradient-to-r from-[var(--brand-pink)] to-transparent lg:block" />
          </div>
        </div>
      </section>
    );
  }
}

function Bands({ children }: { children: React.ReactNode }) {
  // Below-fold sections share a dark surface like the mockup. Trust strip
  // and newsletter render their own pink pill bg overrides inside.
  return (
    <div className="bg-[var(--brand-ink)] px-4 py-12 text-[var(--brand-paper)] sm:px-6 sm:py-16 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-12">{children}</div>
    </div>
  );
}

function PillarsStrip() {
  const { t } = useLocale();
  const items = [
    {
      Icon: PlayCircle,
      title: t("landing.pillars.free_eps"),
      sub: t("landing.pillars.free_eps.sub"),
    },
    {
      Icon: LockKeyhole,
      title: t("landing.pillars.unlock_per_ep"),
      sub: t("landing.pillars.unlock_per_ep.sub"),
    },
    { Icon: Gift, title: t("landing.pillars.bundle"), sub: t("landing.pillars.bundle.sub") },
  ];
  return (
    <section className="grid gap-3 sm:grid-cols-3">
      {items.map(({ Icon, title, sub }) => (
        <div
          key={title}
          className="flex items-center gap-4 rounded-full bg-[var(--brand-pink)] px-6 py-5 text-[var(--brand-ink)]"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--brand-ink)]">
            <Icon className="h-6 w-6 text-white" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-base font-bold leading-tight">{title}</p>
            <p className="truncate text-sm text-[var(--brand-ink)]/70">{sub}</p>
          </div>
        </div>
      ))}
    </section>
  );
}

function TrendingSection({ apiItems }: { apiItems: SeriesItem[] }) {
  const { t, locale } = useLocale();
  // Prefer the seeded series (data flows). Posters fall back to local
  // placeholders mapped by slug; if no seed match exists we just render the
  // placeholder catalog directly.
  const items = PLACEHOLDER_TRENDING.map((p) => {
    // TODO(client-asset): promote to /api/series once real posters exist
    // with these slugs. For now the catalog drives the labels exactly so
    // the trending row matches the mockup ordering. Real series carry their
    // own taglines from the admin copy editor; placeholders fall through.
    const match = apiItems.find((s) => s.slug === p.slug);
    return {
      id: match?._id ?? p.slug,
      slug: match?.slug ?? p.slug,
      titleTh: match?.titleTh ?? p.titleTh,
      titleEn: match?.titleEn ?? p.titleEn,
      taglineTh: match?.taglineTh ?? p.taglineTh,
      taglineEn: match?.taglineEn ?? p.taglineEn,
      posterUrl: p.poster,
    };
  });

  return (
    <section className="space-y-4">
      <TrendingRow
        title={t("landing.trending.title")}
        seeAllHref="/series"
        seeAllLabel={t("landing.trending.see_all")}
      >
        {items.slice(0, 10).map((s, i) => (
          <VerticalPosterCard
            key={s.id}
            href={`/series/${s.slug}`}
            titleTh={s.titleTh}
            titleEn={s.titleEn}
            taglineTh={s.taglineTh}
            taglineEn={s.taglineEn}
            posterUrl={s.posterUrl}
            rank={i + 1}
            locale={locale}
            freeEpisodeCount={3}
          />
        ))}
      </TrendingRow>
    </section>
  );
}

function TrustStrip() {
  const { t } = useLocale();
  const items = [
    { Icon: Heart, label: t("trust.pay_once") },
    { Icon: ShieldCheck, label: t("trust.safe") },
    { Icon: Smartphone, label: t("trust.any_device") },
    { Icon: CircleSlash2, label: t("trust.no_ads") },
  ];
  return (
    <section className="flex flex-col items-stretch justify-around gap-2 rounded-2xl border border-black/5 bg-[var(--brand-paper)] px-6 py-4 text-[var(--brand-ink)] shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:flex-row sm:items-center">
      {items.map(({ Icon, label }) => (
        <div key={label} className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-[var(--brand-rose)]" strokeWidth={2} aria-hidden />
          <p className="text-sm font-medium text-[var(--brand-ink)]">{label}</p>
        </div>
      ))}
    </section>
  );
}

function NewsletterSignup() {
  const { t } = useLocale();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      await fetch("/api/newsletter", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="rounded-3xl bg-[var(--brand-pink-soft)] p-6 text-[var(--brand-ink)] sm:p-10">
      <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold">{t("newsletter.title")}</h2>
          <p className="mt-2 text-sm text-[var(--brand-muted)]">{t("newsletter.subtitle")}</p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("newsletter.placeholder")}
            required
            className="sm:w-72"
            aria-label={t("newsletter.placeholder")}
          />
          <Button type="submit" disabled={status === "sending"}>
            {status === "success" ? t("newsletter.success") : t("newsletter.cta")}
          </Button>
        </form>
      </div>
    </section>
  );
}
